import { propertyMedia } from '@/lib/property-media';
import { PropertyPicture } from '@/components/property/property-media';
import { Header } from "@/components/layout/header";
import { ClickableCard } from "@/components/property/clickable-card";
import { LastMinuteOffer } from "@/components/promotions/last-minute-offer";
import { Footer } from "@/components/layout/footer";
import { BookingWithOffers } from "@/components/promotions/booking-with-offers";
import { BookingOfferPopup } from "@/components/booking/booking-offer-popup";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { PhotoGallery } from "@/components/property/photo-gallery";
// Imported directly, not lazily.
//
// This page is server-rendered, and renderToString cannot resolve React.lazy: the
// component suspends, the server emits the skeleton fallback and leaves the
// Suspense boundary unfinished, and the browser reports React #419 on every load.
//
// The cost was not only the error. The reviews - 48 of them, with the rating -
// were absent from the server HTML entirely, so crawlers and answer engines never
// saw the strongest trust signal on the page. Deferring a below-the-fold section
// is a reasonable trade on a client-rendered page and a bad one here.
import ReviewsSection from "@/components/property/reviews-section";
import { ScrollReveal, StaggeredReveal } from "@/components/ui/scroll-reveal";
import { useQuery } from "@tanstack/react-query";
import { Users, Bed, Waves, Home, ArrowRight, MapPin, Plane, BadgeCheck } from "lucide-react";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { Suspense } from "react";
import { useParallax } from "@/hooks/use-parallax";
import { useSEO, useSEOSettings, DEFAULT_SEO_SETTINGS, generateBreadcrumbSchema, generateFAQSchema, generateKnowledgeGraphSchema, LOCATION_KEYWORDS, type SiteInfoSettings } from "@/lib/seo-utils";
import { LazyVideo } from "@/components/ui/lazy-video";
import { parallaxBg, wavesVideo, wavesPoster, beachShoreline, portfolioHeroVideo, portfolioHeroPoster, portfolioShoreline } from "@/lib/media-assets";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";
import type { PublicProperty } from "@shared/schema";
import { useIsMobile } from "@/hooks/use-mobile";
import { ViatorWidget } from "@/components/property/viator-widget";
import { SkeletonCard } from "@/components/ui/luxury-skeleton";
import { SectionHeading } from "@/components/ui/section-heading";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { useBookCta } from "@/hooks/use-book-href";
import { BOOK_DIRECT_SAVINGS } from "@shared/property-facts";
import { PortfolioWordmark } from "@/components/layout/portfolio-wordmark";
import { KUALOA_PROPERTY_SLUG, KUALOA_STAY_PATH } from "@shared/property-context";
import { RateLine } from "@/components/property/rate-line";
import { KUALOA_FALL_PROMO, isKualoaFallPromoVisible, kualoaFallPromoPercentOff } from "@shared/promotions";
import { TrustLine } from "@/components/property/trust-line";
import { NextOpenWindows } from "@/components/property/next-open-windows";
import { NewListingBadge } from "@/components/property/new-listing-badge";

const heroVideoPath = "/videos/oahu-beachfront-vacation-rental-kualoa-beach-house.mp4";
const heroPoster = "/videos/oahu-beachfront-vacation-rental-kualoa-beach-house-poster.jpg";

const PORTFOLIO_HOME_FAQS = [
  {
    question: "Where are the homes?",
    answer: "On Oahu's windward coast in Ka'a'awa, across from Kualoa Ranch with Mokoli'i (Chinaman's Hat) offshore, about 23 miles (30 minutes) from Honolulu airport.",
  },
  {
    question: "Do I book both homes together?",
    answer: "No. Each home is reserved on its own page. Book one stay, or book both if you need more space for a larger group.",
  },
  {
    question: "Why book these Ka'a'awa homes direct?",
    answer: BOOK_DIRECT_SAVINGS,
  },
];

const TRAVEL_GUIDE_HUBS = [
  { label: "Beaches & Coastal Guides", slug: "beaches-coastal-guides" },
  { label: "Snorkeling & Water Activities", slug: "snorkeling-water-activities" },
  { label: "Hiking & Outdoor Adventure", slug: "hiking-outdoor-adventure" },
  { label: "Food & Local Culture", slug: "food-local-culture" },
  { label: "Trip Planning & Itineraries", slug: "trip-planning-itineraries" },
  { label: "Transportation & Practical Guides", slug: "transportation-practical-guides" },
];

const btnYellow = "w-full sm:w-auto inline-flex items-center justify-center gap-[9px] font-sans font-semibold text-[13px] tracking-[0.08em] uppercase px-[30px] py-[15px] rounded-none border-2 border-transparent bg-sun text-shell shadow-[0_3px_14px_rgba(23, 49, 28,0.28)] hover:bg-[#0f2513] hover:-translate-y-[2px] active:translate-y-0 active:scale-[0.98] transition-all duration-150 cursor-pointer no-underline";
const btnInk = "w-full sm:w-auto inline-flex items-center justify-center gap-[9px] font-sans font-semibold text-[13px] tracking-[0.08em] uppercase px-[30px] py-[15px] rounded-none border-2 border-transparent bg-ink text-shell hover:bg-[#0f2513] hover:-translate-y-[2px] active:translate-y-0 active:scale-[0.98] transition-all duration-150 cursor-pointer no-underline";
const btnGhostDark = "w-full sm:w-auto inline-flex items-center justify-center gap-[9px] font-sans font-semibold text-[13px] tracking-[0.08em] uppercase px-[30px] py-[15px] rounded-none border-2 border-ink bg-transparent text-ink hover:bg-ink hover:text-shell hover:-translate-y-[2px] active:translate-y-0 active:scale-[0.98] transition-all duration-150 cursor-pointer no-underline";
const btnGhostLight = "w-full sm:w-auto inline-flex items-center justify-center gap-[9px] font-sans font-semibold text-[13px] tracking-[0.08em] uppercase px-[30px] py-[15px] rounded-none border-2 border-white/70 bg-transparent text-white hover:bg-white hover:text-ink hover:-translate-y-[2px] active:translate-y-0 active:scale-[0.98] transition-all duration-150 cursor-pointer no-underline";

function homeCardImage(home: PublicProperty): { url: string; alt: string } {
  const featured = home.photos?.find((photo) => photo.is_featured) || home.photos?.[0];
  if (home.hero_image_url) {
    return { url: home.hero_image_url, alt: `${home.title} in Ka'a'awa, Oahu` };
  }
  if (featured?.url) {
    return { url: featured.url, alt: featured.alt || `${home.title} in Ka'a'awa, Oahu` };
  }
  return { url: portfolioShoreline, alt: `Ka'a'awa shoreline, ${home.title}` };
}

function ParallaxBackground({ placeOnly = false }: { placeOnly?: boolean }) {
  const { ref: bgRef, y: bgY } = useParallax<HTMLDivElement>({ speed: 0.3 });
  const { ref: overlayRef, y: overlayY } = useParallax<HTMLDivElement>({ speed: 0.1 });
  const isMobile = useIsMobile();

  const mobileBgY = isMobile ? 0 : bgY;
  const mobileOverlayY = isMobile ? 0 : overlayY;

  return (
    <>
      <motion.div
        ref={bgRef}
        className="absolute inset-0 gpu-accelerated"
        style={{ y: mobileBgY }}
        initial={{ scale: isMobile ? 1 : 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: isMobile ? 0.8 : 1.5, ease: "easeOut" }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <LazyVideo
          src={placeOnly ? portfolioHeroVideo : heroVideoPath}
          poster={optimizedUrl(placeOnly ? portfolioHeroPoster : heroPoster, 1920, 90)}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          className={`w-full h-full object-cover ${placeOnly || !isMobile ? 'object-center' : 'object-[70%_75%]'}`}
          style={{ height: '100%', aspectRatio: 'auto' }}
          width="1280"
          height="720"
          aria-label={
            placeOnly
              ? "The Ko'olau mountains meeting the turquoise water of Kaneohe Bay on Oahu's windward coast"
              : "The Kualoa Beach House, an oceanfront vacation rental in Ka'a'awa on Oahu's windward coast, with palms swaying over calm tropical water"
          }
          onLoadedData={() => {
            performance.mark('hero-video-loaded');
          }}
        />
      </motion.div>
      <motion.div
        ref={overlayRef}
        className="absolute inset-0 gpu-accelerated z-10"
        style={{
          y: mobileOverlayY,
          // Lighter over the coast so the sky and water read; the text carries its own shadow.
          background: placeOnly
            ? 'linear-gradient(180deg, transparent 55%, rgba(20,20,16,0.16) 100%)'
            : 'rgba(20,20,16,0.08)'
        }}
      />
    </>
  );
}

export default function HomePage() {
  const flags = useFeatureFlags();
  return <KualoaHomePage portfolio={Boolean(flags.portfolioHomepage && flags.publishedHomeCount > 1)} />;
}

function KualoaHomePage({ portfolio = false }: { portfolio?: boolean }) {
  const bookCta = useBookCta();
  // Stops rendering on its own the day after the last eligible night; the
  // offer is never shown on the portfolio view, which is not one home's page.
  const showFallPromo = !portfolio && isKualoaFallPromoVisible();
  const { data: property } = useQuery<PublicProperty>({
    queryKey: ["/api/property/public"],
  });
  const { data: homes = [] } = useQuery<PublicProperty[]>({
    queryKey: ["/api/properties"],
    enabled: portfolio,
  });

  // The nightly rate for structured data comes from the property record, which
  // already resolves today's seasonal rate server-side.
  //
  // This used to quote /api/quote for tomorrow-to-the-day-after: a ONE night
  // stay, below the four-night minimum, so it returned 400 on every page load
  // and `pricing` was always undefined. The only consumer was the schema below,
  // which is gated on it - so the property published no price at all to Google
  // or to any answer engine, while a failed request fired on every visit.

  const { seoSettings } = useSEOSettings();
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ['/api/settings/site_info'],
    retry: false,
  });

  const seoHead = useSEO({
    pageType: "home",
    title: portfolio
      ? "Oahu Beachfront Vacation Rentals | Vacation Rental Oahu"
      : DEFAULT_SEO_SETTINGS.page_titles.home,
    description: portfolio
      ? "Beachfront vacation homes on Oahu's windward coast, booked direct with no listing-site fees. Compare each home, check dates and reserve."
      : DEFAULT_SEO_SETTINGS.meta_descriptions.home,
    keywords: `${LOCATION_KEYWORDS.homePrimary}, ${LOCATION_KEYWORDS.locations}, ${LOCATION_KEYWORDS.amenities}, ${LOCATION_KEYWORDS.activities}, ${LOCATION_KEYWORDS.attractions}`,
    ogType: "website",
    ogImage: portfolio
      ? "https://vacationrentaloahu.co/images/oahu/mokolii-aerial.jpg"
      : "https://vacationrentaloahu.co/images/og-sharing-image.jpg",
    twitterCard: "summary_large_image",
    canonical: "https://vacationrentaloahu.co/",
    ogTitle: portfolio ? "Oahu Beachfront Vacation Rentals | Vacation Rental Oahu" : undefined,
    ogDescription: portfolio
      ? "Beachfront vacation homes on Oahu's windward coast. Compare each home and book direct."
      : undefined,
    structuredData: seoSettings && property ? [
      generateKnowledgeGraphSchema({ property, seoSettings, siteInfoSettings: siteSettings }),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" }
      ]),
      ...(portfolio ? [generateFAQSchema(PORTFOLIO_HOME_FAQS)] : []),
      ...(portfolio ? [{
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Vacation Rental Oahu homes",
        itemListElement: homes.map((home, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://vacationrentaloahu.co/properties/${home.slug}`,
          name: home.title,
        })),
      }] : []),
      ...(!portfolio ? [{
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: "The Kualoa Beach House - Oahu Beachfront Vacation Rental",
        description: "Aerial and shoreline footage of The Kualoa Beach House, a beachfront vacation rental in Ka'a'awa on Oahu's windward coast, with palms and calm tropical water.",
        thumbnailUrl: ["https://vacationrentaloahu.co/videos/oahu-beachfront-vacation-rental-kualoa-beach-house-poster.jpg"],
        contentUrl: "https://vacationrentaloahu.co/videos/oahu-beachfront-vacation-rental-kualoa-beach-house.mp4",
        uploadDate: "2026-07-18",
        duration: "PT10S",
      }] : [])
    ] : []
  });

  return (
    <>
    {seoHead}
    <div className="min-h-screen bg-shell pb-20 lg:pb-0 font-sans">
      <Header />

      <main id="main-content">
        <section data-hero-header className="hero-home relative flex items-center overflow-hidden pt-28 hero-content-stable">
          <ParallaxBackground placeOnly={portfolio} />

          <div className="relative z-10 container mx-auto px-5 md:px-8 max-w-[1040px] w-full h-full min-h-0 flex items-start lg:items-center pt-6 pb-8 lg:py-0">
            {/* Phones and tablets: centered column, text near the top and the button near the bottom. Desktop: left column. */}
            <motion.div
              className="text-center lg:text-left w-full max-w-[560px] mx-auto lg:mx-0 flex flex-col h-full lg:h-auto min-h-0 lg:block"
              initial={false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true, amount: 0.2 }}
            >

              <motion.div
                className="media-copy-panel p-5 md:p-7"
                initial={false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                {/* The visible wordmark is the brand only, so the H1's accessible
                    name was "The Kualoa Beach House" - a phrase nobody searches
                    unless they already know us. Naming the category here as well
                    gives screen readers, crawlers and AI answer engines the same
                    fact the title tag states, without altering the hero design. */}
                <h1
                  aria-label={
                    portfolio
                      ? "Vacation Rental Oahu: beachfront vacation homes on Oahu's windward coast"
                      : "The Kualoa Beach House - an Oahu beachfront vacation rental in Ka'a'awa"
                  }
                  className="flex flex-col items-center lg:items-start"
                >
                  {portfolio ? (
                    <PortfolioWordmark size="hero" tone="gold" className="mx-auto lg:mx-0" />
                  ) : (
                    <>
                      <span className="wordmark-gold font-serif italic font-[500] leading-none text-[clamp(30px,7vw,46px)]">
                        The
                      </span>
                      <span className="wordmark-gold font-serif font-[600] leading-[0.86] tracking-[0.01em] text-[clamp(86px,19vw,142px)] -mt-[0.04em]">
                        Kualoa
                      </span>
                      <span className="wordmark-gold font-serif uppercase font-[500] tracking-[0.26em] leading-none text-[clamp(23px,5.4vw,42px)] mt-[0.16em]">
                        Beach House
                      </span>
                    </>
                  )}
                </h1>

                <div className="flex items-center gap-3 mt-[26px] mb-[20px] w-full max-w-[min(88vw,540px)] mx-auto lg:mx-0">
                  <span className="h-px flex-1 bg-[#E3CF98]/55" />
                  <span className="w-[7px] h-[7px] rotate-45 bg-[#E3CF98]" />
                  <span className="h-px flex-1 bg-[#E3CF98]/55" />
                </div>

                <p className="wordmark-gold font-serif italic font-[500] text-[clamp(18px,4.2vw,26px)] leading-[1.35] max-w-[520px] mx-auto lg:mx-0">
                  Beachfront Living Beneath Oʻahu's Most Iconic Mountains.
                </p>
              </motion.div>

              <StaggeredReveal
                className="hidden lg:flex flex-wrap gap-[12px] mt-[30px] mb-[30px] justify-center lg:justify-start"
                staggerDelay={0.1}
                distance={20}
              >
                <div className="flex items-center text-[#E6D2A0] text-[12px] font-sans font-semibold uppercase tracking-[0.08em] media-copy-panel px-[16px] py-[10px] rounded-none border border-[#E6D2A0]/20">
                  {portfolio ? <Home className="h-[16px] w-[16px] mr-[8px] text-current" /> : <Users className="h-[16px] w-[16px] mr-[8px] text-current" />}
                  <span className="wordmark-gold">{portfolio ? "Oceanfront Homes" : property?.max_guests ? `Sleeps ${property.max_guests}` : "Sleeps 8"}</span>
                </div>
                <div className="flex items-center text-[#E6D2A0] text-[12px] font-sans font-semibold uppercase tracking-[0.08em] media-copy-panel px-[16px] py-[10px] rounded-none border border-[#E6D2A0]/20">
                  {portfolio ? <Waves className="h-[16px] w-[16px] mr-[8px] text-current" /> : <Bed className="h-[16px] w-[16px] mr-[8px] text-current" />}
                  <span className="wordmark-gold">{portfolio ? "Windward Oahu" : property?.bedrooms ? `${property.bedrooms} Beds` : "3 Beds"}</span>
                </div>
                <div className="flex items-center text-[#E6D2A0] text-[12px] font-sans font-semibold uppercase tracking-[0.08em] media-copy-panel px-[16px] py-[10px] rounded-none border border-[#E6D2A0]/20">
                  {portfolio ? <Users className="h-[16px] w-[16px] mr-[8px] text-current" /> : <Home className="h-[16px] w-[16px] mr-[8px] text-current" />}
                  <span className="wordmark-gold">{portfolio ? "Book Direct" : "Beachfront"}</span>
                </div>
              </StaggeredReveal>

              <motion.div
                className="flex flex-col sm:flex-row gap-[16px] justify-center lg:justify-start mt-auto lg:mt-0 w-full"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <a href="#check-availability" className={btnYellow} data-primary-book-cta data-testid="book-now-button">
                  {portfolio ? "Choose a Home" : "Book Now"}
                </a>
                <Link
                  href={portfolio ? "/properties" : KUALOA_STAY_PATH}
                  className={`hidden lg:inline-flex ${btnGhostLight}`}
                  data-testid="explore-button"
                >
                  {portfolio ? "See Our Homes" : "Explore Beach House"}
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/*
          Direct-answer block. The hero states the brand and a mood line, which
          tells neither a first-time visitor nor an AI answer engine what this
          place actually is. AEO guidance wants the core question resolved in
          roughly 40-60 words before any preamble, as self-contained sentences a
          model can quote and attribute. Guest count reads from the property
          record rather than a literal, so it cannot drift out of sync the way
          the llms.txt capacity did.
        */}
        {portfolio ? (
          <section className="bg-shell border-b border-sand-deep" aria-label="About Vacation Rental Oahu">
            <div className="mx-auto w-full max-w-[1040px] px-5 md:px-8 py-12 md:py-16">
              <div className="grid gap-8 md:grid-cols-[1fr_1.35fr] md:gap-14 items-center">
                <div className="text-center md:text-left">
                  <p className="text-[11px] md:text-xs font-sans font-normal uppercase tracking-[0.3em] text-driftwood mb-3">
                    Vacation Rental Oahu
                  </p>
                  <h2 className="font-serif font-normal text-forest tracking-tight leading-[1.1] text-[clamp(28px,4vw,40px)]">
                    Oceanfront homes on Oahu's windward coast, booked direct.
                  </h2>
                </div>
                <div>
                  <p className="text-[16px] md:text-[17px] text-ink-soft font-sans font-light leading-relaxed text-center md:text-left">
                    Our homes sit on the water in Ka'a'awa, across from Kualoa Ranch with Mokoli'i (Chinaman's Hat)
                    offshore. Every stay is reserved direct with the hosts, with no platform fees.
                  </p>
                  <ul className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-3 text-left">
                    <li className="flex items-center gap-3 border border-sand-deep bg-sand/60 px-4 py-3">
                      <MapPin className="h-[18px] w-[18px] text-palm shrink-0" strokeWidth={1.5} aria-hidden="true" />
                      <span className="font-sans text-[13px] leading-snug text-ink">Ka'a'awa, on the windward coast</span>
                    </li>
                    <li className="flex items-center gap-3 border border-sand-deep bg-sand/60 px-4 py-3">
                      <Plane className="h-[18px] w-[18px] text-palm shrink-0" strokeWidth={1.5} aria-hidden="true" />
                      <span className="font-sans text-[13px] leading-snug text-ink">23 miles (30 minutes) from HNL airport</span>
                    </li>
                    <li className="flex items-center gap-3 border border-sand-deep bg-sand/60 px-4 py-3">
                      <BadgeCheck className="h-[18px] w-[18px] text-palm shrink-0" strokeWidth={1.5} aria-hidden="true" />
                      <span className="font-sans text-[13px] leading-snug text-ink">Book direct, no platform fees</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        ) : (
        <section className="bg-shell border-b border-sand-deep" aria-label="About this rental">
          <div className="mx-auto w-full max-w-[860px] px-5 md:px-8 py-8 md:py-10">
            <p className="text-[16px] md:text-[17px] text-ink-soft font-sans font-light leading-relaxed text-center">
              {portfolio ? (
                <>
                  <strong className="font-normal text-ink">Vacation Rental Oahu</strong> offers oceanfront vacation homes.
                </>
              ) : (
                <>
                  <strong className="font-normal text-ink">The Kualoa Beach House</strong> is a {property?.bedrooms ?? 3}-bedroom
                  oceanfront vacation rental in Ka'a'awa, on Oahu's windward coast. It sleeps {property?.max_guests ?? 8},
                  opens onto a private stretch of beach, and sits directly across from Kualoa Ranch with Mokoli'i
                  (Chinaman's Hat) offshore. It is about 23 miles (30 minutes) from Honolulu airport. Book direct with no
                  platform fees and instant confirmation.
                </>
              )}
            </p>
          </div>
        </section>
        )}

        {showFallPromo ? (
          <section
            className="bg-shell border-b border-sand-deep"
            aria-labelledby="kualoa-fall-promo-heading"
            data-testid="kualoa-promo-primary"
          >
            <div className="mx-auto w-full max-w-[860px] px-5 md:px-8 py-9 md:py-12">
              <div className="border border-sand-deep bg-sand px-6 py-7 md:px-10 md:py-9">
                <p className="font-sans text-[11px] tracking-[0.16em] uppercase text-driftwood">
                  Limited Time
                </p>
                <h2
                  id="kualoa-fall-promo-heading"
                  className="mt-3 font-serif text-[26px] md:text-[32px] leading-tight text-forest"
                >
                  Limited-Time Kualoa Beach Special
                </h2>
                <p className="mt-3 font-sans text-[15px] md:text-[16px] leading-relaxed text-ink-soft">
                  Save {kualoaFallPromoPercentOff()}% on stays from September 14 through October 14, 2026.
                </p>
                <div className="mt-5 flex flex-wrap items-baseline gap-x-5 gap-y-1">
                  <span className="font-sans text-[14px] text-ink-soft line-through decoration-driftwood/60">
                    Regularly ${KUALOA_FALL_PROMO.regularNightly}/night
                  </span>
                  <span className="font-serif text-[22px] md:text-[25px] leading-tight text-forest">
                    Special rate: ${KUALOA_FALL_PROMO.promoNightly.toFixed(2)}/night
                  </span>
                </div>
                <div className="mt-7">
                  <a
                    href="#check-availability"
                    className={btnYellow}
                    data-primary-book-cta
                    data-testid="kualoa-promo-cta"
                  >
                    Check Availability
                  </a>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        <section
          id="check-availability"
          data-sticky-book-anchor
          className="bg-sand border-y border-sand-deep scroll-mt-28"
          aria-label={portfolio ? "Choose a home" : "Check availability"}
        >
          {portfolio ? (
            <div className="mx-auto w-full max-w-[1040px] px-5 md:px-8 py-10 md:py-14">
              <SectionHeading
                eyebrow="The Homes"
                title="Two beachfront homes on windward Oahu"
                lede="Oceanfront stays with views of Mokoli'i and the Ko'olau range."
                align="center"
              />
              <ul className="mt-2 flex gap-4 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-5 px-5 pb-2 md:mx-0 md:px-0 md:pb-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible">
                {homes.map((home) => {
                  const image = homeCardImage(home);
                  return (
                    <ClickableCard key={home.id} href={`/properties/${home.slug}`} className="group flex flex-col snap-start shrink-0 w-[86%] md:w-auto border border-sand-deep bg-shell text-center overflow-hidden transition-shadow hover:shadow-lg" data-testid={`home-page-card-${home.slug}`}>
                      <div className="relative">
                        <NewListingBadge slug={home.slug} variant="overlay" />
                        {propertyMedia(home.slug) ? <PropertyPicture media={propertyMedia(home.slug)!} alt={image.alt} className="w-full h-60 object-cover md:h-64" /> : (
                        <img
                          src={optimizedUrl(image.url, 900)}
                          srcSet={optimizedSrcSet(image.url, [480, 720, 900])}
                          sizes="(min-width: 768px) 50vw, 100vw"
                          alt={image.alt}
                          width={900}
                          height={600}
                          className="w-full h-52 object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        )}
                      </div>
                      {/* Column body with the buttons pinned to the foot and the blurb
                          capped at three lines, so both cards' buttons sit level. */}
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="font-serif text-[24px] text-forest leading-tight">
                          <Link href={`/properties/${home.slug}`} className="hover:text-forest/70 transition-colors">{home.title}</Link>
                        </h3>
                        <p className="mt-2 font-sans text-[14px] text-ink/70">
                          {home.bedrooms} bedrooms · {home.bathrooms} bathrooms · up to {home.max_guests} guests
                        </p>
                        <p className="mt-3 line-clamp-3 font-sans text-[15px] leading-relaxed text-ink-soft">
                          {home.short_description || home.marketing_description || "A beachfront Ka'a'awa home you book direct."}
                        </p>
                        <RateLine home={home} className="mt-4" testId={`home-rate-${home.slug}`} />
                        <TrustLine home={home} className="mt-2 justify-center" />
                        <LastMinuteOffer slug={home.slug} variant="badge" className="mt-3 self-center" />
                        <NextOpenWindows slug={home.slug} className="mt-2" />
                        <div className="mt-auto flex flex-wrap justify-center gap-3 pt-5">
                          <Link
                            href={`/properties/${home.slug}`}
                            className={btnInk}
                            aria-label={`View Property: ${home.title}`}
                          >
                            View Property
                          </Link>
                          <Link
                            href={`/properties/${home.slug}/book?open=dates`}
                            className={btnGhostDark}
                            aria-label={`Book Now: ${home.title}`}
                            data-primary-book-cta
                          >
                            Book Now
                          </Link>
                        </div>
                      </div>
                    </ClickableCard>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8 py-5 md:py-6">
              {showFallPromo ? (
                <div
                  className="mb-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border border-sand-deep bg-shell px-4 py-3 md:px-5"
                  data-testid="kualoa-promo-booking"
                >
                  <p className="font-sans text-[11px] font-semibold tracking-[0.14em] uppercase text-driftwood">
                    {kualoaFallPromoPercentOff()}% off September 14 &ndash; October 14
                  </p>
                  <p className="font-sans text-[14px] text-ink-soft">
                    Special rate:{" "}
                    <span className="font-semibold text-forest">
                      ${KUALOA_FALL_PROMO.promoNightly.toFixed(2)}/night
                    </span>
                  </p>
                </div>
              ) : null}
              <BookingWithOffers layout="inline" propertyId={property?.id} propertySlug={property?.slug ?? undefined} />
            </div>
          )}
        </section>

        {!portfolio ? <PhotoGallery /> : null}

        <section className="py-14 md:py-[82px] bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-5 md:px-8 max-w-[1040px]">
            <SectionHeading
              eyebrow="The Idea"
              title="Experience the Real Hawaii"
              lede={portfolio
                ? "Where authentic island culture meets beachfront comfort at our Ka'a'awa homes."
                : "Where authentic island culture meets pristine beachfront comfort at The Kualoa Beach House."}
              align="center"
            />

            <div className="grid md:grid-cols-2 gap-x-[56px] gap-y-[36px] mt-[42px]">
              <ScrollReveal delay={0.2}>
                <div>
                  <div className="w-10 h-px bg-ink/25 mb-[22px]" />
                  <h3 className="font-serif font-normal text-forest text-[24px] mb-[12px] leading-[1.1]">Secluded Beach Paradise</h3>
                  <p className="text-[15px] text-ink-soft font-sans leading-relaxed">
                    Enjoy a peaceful 2-mile beach walk without the crowds. Our pristine beachfront location offers
                    the tranquility and privacy you're seeking, far from tourist-packed shores.
                  </p>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={0.3}>
                <div>
                  <div className="w-10 h-px bg-ink/25 mb-[22px]" />
                  <h3 className="font-serif font-normal text-forest text-[24px] mb-[12px] leading-[1.1]">True Hawaiian Culture</h3>
                  <p className="text-[15px] text-ink-soft font-sans leading-relaxed">
                    Immerse yourself in authentic island life. Experience the real Hawaii the way locals live,
                    away from commercialized tourism, in a genuine Hawaiian neighborhood.
                  </p>
                </div>
              </ScrollReveal>
            </div>

            <ScrollReveal delay={0.4}>
              <div className="mt-[44px] pt-[40px] border-t border-sand-deep max-w-[760px] mx-auto text-center">
                <h3 className="font-serif font-normal text-forest text-[24px] mb-[12px] leading-[1.1]">Best of Both Worlds</h3>
                <p className="text-[15px] text-ink-soft font-sans mb-[26px] leading-relaxed max-w-[620px] mx-auto">
                  While our beachfront sanctuary offers peaceful seclusion and an authentic Hawaiian experience,
                  you're never far from adventure. World-class dining, shopping, and tourist attractions are just
                  a short, scenic drive away, giving you the freedom to explore when you want.
                </p>
                <div className="w-8 h-[2px] bg-sun mx-auto mb-[18px]" />
                <p className="text-ink font-normal font-serif italic text-[18px] leading-[1.4] max-w-[560px] mx-auto">
                  "For those who seek beachfront comfort with the soul of true Hawaii."
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="scenic-editorial relative overflow-hidden border-t border-sand-deep">
          <div className="absolute inset-0 z-0">
            {portfolio ? (
              /* The Mokoli'i aerial clip behind the getaway band; the poster is
                 its first frame, so nothing shifts while the video loads. */
              <LazyVideo
                src={wavesVideo}
                poster={wavesPoster}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                aria-label="Mokoli'i (Chinaman's Hat) and the Ko'olau range from the air"
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              <img
                src={optimizedUrl(parallaxBg, 1600)}
                srcSet={optimizedSrcSet(parallaxBg, [768, 1200, 1600, 1920])}
                sizes="100vw"
                alt="The Kualoa Beach House exterior facing the Pacific, with palms and Mokoli'i across the water"
                loading="lazy"
                decoding="async"
                width={1920}
                height={1080}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}

          </div>

          <div className="container mx-auto px-5 md:px-8 max-w-[1040px] relative z-10">
            <div className="max-w-2xl mx-auto text-center mb-10 md:mb-14 media-copy-panel p-5 md:p-7">
              <p className="text-[11px] md:text-xs font-sans font-normal uppercase tracking-[0.3em] wordmark-gold mb-3">
                {portfolio ? "The Shoreline" : "The Property"}
              </p>
              <h2 className="font-serif font-[500] wordmark-gold tracking-luxury-tight leading-[1.05] text-[clamp(32px,5.2vw,50px)]">
                Your Oahu Beachfront Getaway
              </h2>
              <p className="mt-4 text-[21px] wordmark-gold leading-relaxed font-serif italic">
                {portfolio
                  ? "Everything you need for the perfect Oahu vacation, at whichever of our beachfront homes fits your group."
                  : "Everything you need for the perfect Oahu vacation experience at The Kualoa Beach House."}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-[20px] mt-[34px]">
              <div className="text-center p-[30px] border border-white/20 rounded-none media-copy-panel">
                <Users className="h-[38px] w-[38px] text-white mx-auto mb-[16px]" strokeWidth={1.5} />
                <h3 className="font-serif font-[500] wordmark-gold text-[24px] mb-[8px] leading-[1.1]">
                  {portfolio ? "Our Homes" : property?.max_guests ? `${property.max_guests} Guests` : 'Spacious for All'}
                </h3>
                <p className="wordmark-gold text-[18px] font-serif">
                  {portfolio ? "Choose the one that fits your group" : "Perfect for families and groups"}
                </p>
              </div>

              <div className="text-center p-[30px] border border-white/20 rounded-none media-copy-panel">
                <Bed className="h-[38px] w-[38px] text-white mx-auto mb-[16px]" strokeWidth={1.5} />
                <h3 className="font-serif font-[500] wordmark-gold text-[24px] mb-[8px] leading-[1.1]">
                  {portfolio ? "Mountain & Ocean Views" : property?.bedrooms ? `${property.bedrooms} Bedrooms` : 'Comfortable Sleeping'}
                </h3>
                <p className="wordmark-gold text-[18px] font-serif">
                  {portfolio ? "The Ko'olau range behind you, Mokoli'i ahead" : "Premium accommodations for everyone"}
                </p>
              </div>

              <div className="text-center p-[30px] border border-white/20 rounded-none media-copy-panel">
                <Waves className="h-[38px] w-[38px] text-white mx-auto mb-[16px]" strokeWidth={1.5} />
                <h3 className="font-serif font-[500] wordmark-gold text-[24px] mb-[8px] leading-[1.1]">
                  Beachfront
                </h3>
                <p className="wordmark-gold text-[18px] font-serif">Direct beach access and stunning views</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-[82px] bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-5 md:px-8 max-w-[1040px] text-center">
            <SectionHeading
              eyebrow="Details"
              title={portfolio ? "Explore Each Home" : "Explore the Full Property"}
              lede={portfolio
                ? "Amenities, photos and house rules for each home, plus the Ka'a'awa area guide to the coast around them."
                : "Amenities, interactive map, house rules, and detailed photos live on our property page, your complete guide before you book."}
              align="center"
            />
            <div className="flex flex-wrap justify-center gap-[16px] mt-[34px]">
              {portfolio ? (
                <>
                  <Link href="/properties#compare" className={btnInk}>
                    Compare Homes
                  </Link>
                  <Link href="/kaaawa" className={btnGhostDark}>
                    Ka'a'awa Area Guide
                  </Link>
                  <Link href={`${KUALOA_STAY_PATH}#location`} className={btnGhostDark}>
                    See Location
                  </Link>
                </>
              ) : (
                <>
                  <Link href={`${KUALOA_STAY_PATH}#amenities`} className={btnGhostDark}>
                    View Amenities
                  </Link>
                  <Link href={`${KUALOA_STAY_PATH}#location`} className={btnGhostDark}>
                    See Location
                  </Link>
                  <Link href={KUALOA_STAY_PATH} className={btnInk}>
                    Full Details
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>

        <ReviewsSection />

        {portfolio && (
          <section className="py-14 md:py-[82px] bg-sand border-t border-sand-deep">
            <div className="container mx-auto px-5 md:px-8 max-w-[760px]">
              <SectionHeading
                eyebrow="Questions"
                title="Staying with Vacation Rental Oahu"
                align="center"
              />
              <dl className="space-y-8">
                {PORTFOLIO_HOME_FAQS.map((faq) => (
                  <div key={faq.question}>
                    <dt>
                      <h3 className="font-serif text-[22px] text-forest leading-tight">{faq.question}</h3>
                    </dt>
                    <dd className="mt-2 font-sans text-[15px] leading-relaxed text-ink-soft">{faq.answer}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        )}

        <section className="py-14 md:py-[82px] bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-5 md:px-8 max-w-[1040px]">
            <SectionHeading
              eyebrow="Guides"
              title="Plan Your Oahu Trip"
              lede="Explore our Oahu Travel Guide: expert articles on beaches, hiking, food, and more from our Ka'a'awa home base."
              align="center"
            />
            <div className="grid sm:grid-cols-2 gap-x-[56px] mt-[42px] border-t border-sand-deep">
              {TRAVEL_GUIDE_HUBS.map((hub, i) => (
                <Link
                  key={hub.slug}
                  href={`/blog/category/${hub.slug}`}
                  className="group flex items-center justify-between gap-5 py-[22px] border-b border-sand-deep transition-colors duration-200 hover:border-ink/40"
                >
                  <div className="flex items-baseline gap-[18px]">
                    <span className="font-serif text-[13px] text-driftwood tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-serif font-normal text-forest text-[19px] md:text-[22px] leading-[1.15] text-left">
                      {hub.label}
                    </h3>
                  </div>
                  <ArrowRight className="h-[18px] w-[18px] flex-shrink-0 text-driftwood transition-all duration-200 group-hover:translate-x-1 group-hover:text-ink" aria-hidden="true" />
                </Link>
              ))}
            </div>
            <div className="text-center mt-[34px] flex flex-wrap gap-[16px] justify-center">
              <Link href="/blog" className={btnGhostDark}>
                All Travel Guides
              </Link>
              <Link href="/kaaawa" className={btnGhostDark}>
                Ka'a'awa Area Guide
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-[82px] bg-shell border-t border-sand-deep">
          <div className="container mx-auto px-5 md:px-8 max-w-[1040px]">
            <SectionHeading
              eyebrow="Activities"
              title="Book Hawaiian Experiences"
              lede="Enhance your stay with unforgettable adventures and activities around Oahu."
              align="center"
            />

            <ScrollReveal className="max-w-[820px] mx-auto mt-[34px]">
              <div className="bg-sand border border-sand-deep rounded-none p-5 md:p-[30px] shadow-[0_12px_44px_rgba(120,100,60,0.07)]">
                <ViatorWidget
                  partnerId="P00269401"
                  widgetRef="W-05b9336c-e7c2-4c23-a749-fff0a44366ea"
                  minHeight="400px"
                />
              </div>

              <div className="text-center mt-[34px]">
                <Link href="/experiences" className={btnInk} data-testid="view-all-experiences-button">
                  View All Experiences
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="scenic-editorial relative overflow-hidden border-t border-sand-deep">
          <div className="absolute inset-0 z-0">
            <LazyVideo
              src={portfolio ? portfolioHeroVideo : wavesVideo}
              poster={portfolio ? portfolioHeroPoster : wavesPoster}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
              style={{ aspectRatio: '16/9' }}
              aria-label={
                portfolio
                  ? "The windward coast beneath the Ko'olau range, from the air"
                  : "Pristine Oahu beach at sunset with turquoise waters and golden sand at The Kualoa Beach House"
              }
            />
            {/* Keep the coast bright; contrast is localized to the copy below. */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 60%, rgba(20,20,16,.18) 100%)' }}></div>
          </div>
          <div className="container mx-auto px-5 md:px-8 max-w-[800px] py-7 text-center relative z-10 flex flex-col items-center media-copy-panel">
            <p className="wordmark-gold text-[11px] md:text-xs font-sans font-normal uppercase tracking-[0.3em] mb-3">
              Reserve
            </p>
            <h2 className="wordmark-gold font-serif font-[500] text-[clamp(32px,7vw,72px)] leading-[1]">
              Ready for Your Hawaiian <span className="block md:inline italic">Paradise?</span>
            </h2>
            <div className="flex items-center gap-3 mt-[18px] mb-[16px] w-full max-w-[min(80vw,420px)] mx-auto">
              <span className="h-px flex-1 bg-[#E3CF98]/55" />
              <span className="w-[7px] h-[7px] rotate-45 bg-[#E3CF98]" />
              <span className="h-px flex-1 bg-[#E3CF98]/55" />
            </div>
            <p className="wordmark-gold text-[21px] mb-[34px] max-w-[600px] font-serif italic">
              {portfolio
                ? "Choose one of our Ka'a'awa beachfront homes and start planning your stay."
                : "Book now and start planning your dream vacation in our luxury Oahu beach house."}
            </p>
            {portfolio ? (
              <a href="#check-availability" className={btnYellow} data-primary-book-cta data-testid="cta-book-button">
                Choose a Home
              </a>
            ) : (
              <Link href={bookCta.href} className={btnYellow} data-primary-book-cta data-testid="cta-book-button">
                Book Your Stay
              </Link>
            )}
          </div>
        </section>

        <StickyMobileBookCta />
      </main>
      <Footer />
      <BookingOfferPopup autoTrigger={false} />
    </div>
    </>
  );
}

